<template>
  <vl-region :key="key">
    <vl-layout>
      <PbsGoBackLink />
      <template v-if="!loading">
        <vl-grid>
          <vl-column width="7" width-xs="12">
            <vl-title tag-name="h1">
              {{ $t('entitymodel.detail.title') }} <span><PbsStatusIcon v-if="detail" :id="detail?.statusId" /></span>
            </vl-title>
          </vl-column>
          <vl-column width="5" width-xs="12">
            <vl-action-group
              v-if="detail && !editStore.isEdit(EntityModelEditType.EntityModel, detail.id)"
              mod-align-right
              mod-space
              between
            >
              <vl-button
                v-if="editStore.canEdit"
                icon="pencil"
                mod-icon-before
                mod-secondary
                :mod-disabled="editStore.editing"
                @click="editStore.openEdit(EntityModelEditType.EntityModel, detail.id)"
              >
                {{ $t('general.edit') }}
              </vl-button>

              <EntityModelActionButton :detail="detail" :actions="actionButtonTypes" />
            </vl-action-group>
            <vl-action-group v-else mod-align-right mod-space between>
              <vl-button icon="save" :mod-loading="reloading" mod-icon-before @click="save">{{
                $t('general.save')
              }}</vl-button>
              <vl-button
                icon="cross"
                :mod-loading="loading"
                mod-icon-before
                mod-secondary
                mod-error
                @click="editStore.closeEdit"
              >
                {{ $t('general.cancel') }}
              </vl-button>
            </vl-action-group>
          </vl-column>
        </vl-grid>
        <vl-grid
          v-if="detail"
          :class="{
            edit: editStore.isEdit(EntityModelEditType.EntityModel, detail.id),
            'vl-u-spacer-bottom--medium': true
          }"
        >
          <vl-column>
            <EntityModelEdit v-if="editStore.isEdit(EntityModelEditType.EntityModel, detail.id)" ref="editForm" />
            <EntityModelRead v-else />
          </vl-column>
        </vl-grid>
      </template>
      <template v-if="detail && !loading">
        <EntityAttribute :model="detail" />
        <template v-if="isCodeList && isSourceContextType"> <CodeListAttributeMapping :model="detail" /> </template>
        <div class="vl-u-spacer--large vl-u-visible--s"></div>
        <EntityModelDependencies :id="detail.id" :title="$t('entitymodel.dependencies.title')" />
      </template>

      <template v-if="loading">
        <vl-region>
          <div v-vl-align:center>
            <vl-loader :message="$t('entitymodel.detail.loading')" />
          </div>
        </vl-region>
      </template>
    </vl-layout>
  </vl-region>
</template>
<script lang="ts" setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useRoute, onBeforeRouteLeave } from 'vue-router';
import { useEntityModelDetailStore } from '../../store/entity-model/entity-model-detail.store';
import { useEntityModelEditStore, EntityModelEditType } from '../../store/entity-model/entity-model-edit.store';
import { EntityModelDetailDto } from '@/api/backoffice/clients';
import CodeListAttributeMapping from './subcomponents/code-list-attribute-mapping/CodeListAttributeMapping.vue';
import EntityModelDependencies from './subcomponents/entity-model-dependencies/EntityModelDependencies.vue';
import EntityModelActionButton from './subcomponents/entity-model-action-button/EntityModelActionButton.vue';
import EntityAttribute from './subcomponents/EntityAttribute.vue';
import EntityModelRead from './EntityModelRead.vue';
import EntityModelEdit from './EntityModelEdit.vue';
import PbsGoBackLink from '@/components/PbsGoBackLink.vue';
import PbsStatusIcon from '@/components/PbsStatusIcon.vue';
import { EntityModelActionButtonType, ENUM_ContextTypes } from '../../common';
import { ENUM_EntityTypes } from '@/modules/configuration/common';

const store = useEntityModelDetailStore();
const editStore = useEntityModelEditStore();

const route = useRoute();
const editForm = ref();
const key = ref(route.params.id);

watch(
  () => route.params.id,
  (newKey) => {
    if (key.value !== newKey) {
      key.value = newKey;
      getDetail();
    }
  }
);

const actionButtonTypes = computed(() => {
  return [
    EntityModelActionButtonType.ChangeStatus,
    EntityModelActionButtonType.Export,
    EntityModelActionButtonType.Duplicate,
    EntityModelActionButtonType.CascadeDelete
  ];
});

const detail = computed((): EntityModelDetailDto => {
  return store.detail;
});

const loading = computed((): boolean => {
  return store.loading;
});

const reloading = computed((): boolean => {
  return store.reloading;
});

const isCodeList = computed((): boolean => {
  return detail.value?.entityType.name == ENUM_EntityTypes.CodeList.name;
});

const isSourceContextType = computed((): boolean => {
  return detail.value?.contextType.name == ENUM_ContextTypes.Source.name;
});

const id = computed((): number => {
  return parseInt(route.params.id as string, 10);
});

onMounted(async () => {
  await getDetail();
});

const save = () => {
  editForm.value.onSubmit();
};

const getDetail = async () => {
  editStore.init();
  await store.get(id.value);
};

onBeforeRouteLeave((to, from, next) => {
  const id = parseInt(from.params.id as string, 10);
  if (id && store) {
    store.clear(id);
  }
  next();
});
</script>
<style lang="scss" scoped>
@import '@govflanders/vl-ui-design-system-style/scss/core/setting/_colorsRaw.scss';
@import '@govflanders/vl-ui-design-system-style/scss/core/setting/_colors.scss';

.pbs-form-group {
  background: $vl-alt-bg;
  padding: 3rem;
}
</style>
